import type { SowCreditPoolType } from '@wevo/wevo-access/sow-credit-pools';
import type { components as OpenApiComponents } from '../types/api.generated';
import { useAuth0 } from '@auth0/auth0-react';
import { apiConfig } from '../config/auth0';
import { streamJsonSse } from './api.sse';
import { readApiErrorFromResponse } from '../lib/apiErrors';

const API_BASE_URL = apiConfig.accessApiUrl;

type OpenApiSchemas = OpenApiComponents['schemas'];

export type CreateSowUsageCreditImportRequest = OpenApiSchemas['CreateSowUsageCreditImportRequest'];
export type CreateCreditImportRequest = OpenApiSchemas['CreateCreditImportRequest'];
export type CreateCreditImportResponse = OpenApiSchemas['CreateCreditImportResponse'];
export type CreditImportValidationResponse = OpenApiSchemas['CreditImportValidationResponse'];
export type SowUsageCreditImportValidationResponse = Extract<
    CreditImportValidationResponse,
    {
        summary: OpenApiSchemas['CreditImportValidationSummary'];
        rows: OpenApiSchemas['SowUsageCreditImportValidatedRow'][];
    }
>;
export type CreditImportApplyResponse = OpenApiSchemas['CreditImportApplyResponse'];
export type SowUsageCreditImportApplyResponse = Extract<
    CreditImportApplyResponse,
    { batchId: string; sowId: string; appliedRowCount: number }
>;

interface PublicSignupRequest {
    email: string;
    firstName: string;
    lastName: string;
}

interface CompanyInviteRequest {
    email: string;
    firstName: string;
    lastName: string;
}

interface OnboardingResponse {
    success: true;
    userId?: number;
    auth0UserId?: string;
    companyId?: number;
    organizationId?: string;
    teamId?: number;
}

export interface SowPulseSubscriptionSeatSummary {
    id: string;
    userId?: number | null;
    inUse: boolean;
    assignedAt?: string | null;
    unassignedAt?: string | null;
    activeToDate?: string | null;
}

export interface SowPulseSubscriptionSummary {
    id: string;
    scope: string;
    sowId?: string | null;
    companyId?: number | null;
    teamId?: number | null;
    numSeats: number;
    pulseSubscriptionTierId: string;
    activeFromDate?: string;
    activeToDate?: string | null;
    PulseSeats?: SowPulseSubscriptionSeatSummary[];
}

export interface SowSummary {
    id: string;
    companyId: number;
    /** Present when loaded with company include (e.g. GET /sows/:id). */
    Company?: {
        id: number;
        name: string;
    };
    name: string;
    description?: string | null;
    contractReference?: string | null;
    artifactUrl?: string | null;
    unitMode: 'credit' | 'token';
    state: string;
    startDate: string;
    endDate?: string | null;
    pricingConfigId: string;
    metadata?: Record<string, unknown>;
    writeProtectionEnabled?: boolean;
    /** Whether the authenticated user may create new charges against this SOW. */
    canWrite?: boolean;
    balanceSummary?: SowBalanceSummary | null;
    CreditPools?: SowCreditPoolSummary[];
    SowTeams?: SowTeamSummary[];
    PulseSubscriptions?: SowPulseSubscriptionSummary[];
}

export interface SowTeamSummary {
    id: string;
    sowId: string;
    teamId: number;
    /** Present when loaded with team include (e.g. GET /sows/:id). */
    Team?: {
        id: number;
        name: string;
    };
}

export interface SowWriteAccessUser {
    userId: number;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
}

export interface SowWriteAccessSummary {
    sowId: string;
    enabled: boolean;
    users: SowWriteAccessUser[];
    eligibleUsers: SowWriteAccessUser[];
}

export interface UpdateSowWriteAccessRequest {
    enabled: boolean;
    userIds: number[];
}

export interface SowCreditPoolSummary {
    id: string;
    sowId: string;
    poolType: SowCreditPoolType;
    initialAmount: string;
    resetPolicy?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
}

export interface SowBalancePoolSummary {
    poolType: string;
    resetPolicy?: Record<string, unknown>;
    contracted?: string;
    released?: string;
    expiredByPolicy?: string;
    fromRollover?: string;
    granted: string;
    pending: string;
    committed: string;
    locked: string;
    available: string;
    /** Mirrors balance `pending` / `committed`: usage rows plus rollover_out; excludes grant, rollover_in, and expiration. */
    usagePending: string;
    usageCommitted: string;
    currentPeriodStart?: string | null;
    currentPeriodEnd?: string | null;
    periodSummaries?: Array<{
        index: number;
        startDate: string;
        endDate: string;
        cadence: 'monthly' | 'quarterly' | 'yearly';
        isCurrent: boolean;
        releaseAmount: string;
        usagePending: string;
        usageCommitted: string;
        expired: string;
        carriedIntoNext: string;
        availableAtEnd: string;
    }>;
}

export interface SowBalanceSummary {
    sowId: string;
    companyId: number;
    name: string;
    state: string;
    unitMode: 'credit' | 'token';
    startDate: string;
    endDate?: string | null;
    pools: SowBalancePoolSummary[];
    totals: Omit<SowBalancePoolSummary, 'poolType'>;
}

export interface PricingConfigSummary {
    id: string;
    pricingConfigGroupId: string;
    name: string;
    description?: string | null;
    version: number;
    isActive: boolean;
    config: PricingConfigPayload;
    createdAt?: string;
    updatedAt?: string;
}

/** Unit recorded in `PricingConfigSummary.config` (`credit` vs `token`). */
export type PricingConfigUnitType = 'credit' | 'token';

export interface PricingConfigSampleSize {
    n: number;
    multiplier: number;
}

export interface PricingConfigProSection {
    supportsPricingEstimates: boolean;
    sentimentMapMultiplier: number;
    b2bCredits: number;
    b2cCredits: number;
    sampleSizes: PricingConfigSampleSize[];
    defaultSampleSize: number;
}

export interface PricingConfigPulseSection {
    standardPulse: number;
}

export interface PricingConfigPayload {
    unitType: PricingConfigUnitType;
    pro: PricingConfigProSection;
    pulse: PricingConfigPulseSection;
}

interface PaginatedResponse<T> {
    items: T[];
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
}

export interface AdminListParams {
    query?: string;
    page?: number;
    pageSize?: number;
    /** Restrict users to those on teams for this company. */
    companyId?: number;
    /** When true, only users with `isActive`. */
    activeUsersOnly?: boolean;
    /** When true, only companies with `isActive`. */
    activeCompaniesOnly?: boolean;
}

export interface AdminCreditSowParams extends AdminListParams {
    state?: string;
    /** When true, exclude expired and archived agreements. */
    activeSowsOnly?: boolean;
    /** Admin SOW register: end date (default), Pro usage, or Pulse usage. */
    sortBy?: 'end_date' | 'pro_usage_percent' | 'pulse_usage_percent';
}

export interface AdminCreditPricingConfigParams extends AdminListParams {
    includeInactive?: boolean;
}

export interface AdminProfessionalServiceLinkageRow {
    id: string;
    professionalServiceId: string;
    linkedArtifactType: string;
    linkedArtifactId: string;
    createdAt: string;
    updatedAt: string;
    deletedAt?: string | null;
}

export interface AdminProfessionalServiceRow {
    id: string;
    companyId?: number | null;
    teamId?: number | null;
    teamName?: string | null;
    sowId?: string | null;
    sowName?: string | null;
    name: string;
    description?: string | null;
    createdByUserId?: number | null;
    updatedByUserId?: number | null;
    createdAt: string;
    updatedAt: string;
    deletedAt?: string | null;
    linkages: AdminProfessionalServiceLinkageRow[];
}

export interface AdminProfessionalServiceMutationRequest {
    name: string;
    description?: string | null;
    sowId?: string | null;
    teamId?: number | null;
    linkages: Array<{
        linkedArtifactType: string;
        linkedArtifactId: string;
    }>;
}

export interface AdminCompanyRow {
    companyId: number;
    organizationId?: string | null;
    name: string;
    isActive: boolean;
    sowCount: number;
    activeSowCount: number;
    granted: number;
    available: number;
    used: number;
    usagePercent: number;
}

interface AdminCompanyDetail {
    companyId: number;
    organizationId: string | null;
    name: string;
    industryId: number;
    isActive: boolean;
    customizations: Record<string, unknown>;
}

export interface AdminCompanyAdminStatus {
    userId: number;
    subjectId: string;
    isAdmin: boolean;
    /** Auth0 organization role names; absent from API builds before Manage role editing. */
    roles?: string[];
}

export interface AdminCompanyAdminsResponse {
    companyId: number;
    companyAdmins: AdminCompanyAdminStatus[];
}

export interface AdminCompanyUserAdminState {
    companyId: number;
    organizationId: string;
    userId: number;
    subjectId: string;
    isAdmin: boolean;
    affectedTeamIds: number[];
}

export interface UpdateAdminCompanyUserAdminRequest {
    isAdmin: boolean;
}

export interface AdminCompanyUserAdminUpdateResponse {
    success: boolean;
    companyAdmin: AdminCompanyUserAdminState;
}

export interface CreateAdminCompanyRequest {
    name: string;
    industryId: number;
    isActive: boolean;
    customizations: Record<string, unknown>;
}

export interface AdminCompanyCreateResponse {
    companyId: number;
    organizationId: string | null;
    name: string;
    industryId: number;
    isActive: boolean;
    customizations: Record<string, unknown>;
}

export interface UpdateAdminCompanyRequest {
    name: string;
    isActive: boolean;
    customizations: Record<string, unknown>;
}

export interface AdminUserRow {
    id: number;
    publicId: string;
    subjectId?: string | null;
    email: string;
    name: string;
    firstName: string;
    lastName: string;
    isActive: boolean;
    isSso: boolean;
    isSyncedToAuth0: boolean;
    isSyncableToAuth0?: boolean;
    auth0SyncDisabledReason?: string | null;
    auth0LastLogin?: string | null;
    companyNames: string[];
    teamNames: string[];
    memberships: AdminUserMembership[];
}

export interface AdminUserUpdateResult extends AdminUserRow {
    auth0SyncWarning: string | null;
}

export interface AdminUserMembership {
    companyId: number;
    companyName: string;
    teamId: number;
    teamName: string;
}

export interface AdminUserEditTeamOption {
    teamId: number;
    teamName: string;
}

export interface AdminUserEditCompanyOption {
    companyId: number;
    companyName: string;
    teams: AdminUserEditTeamOption[];
}

export interface UpdateAdminUserRequest {
    firstName: string;
    lastName: string;
    isSso: boolean;
    isActive: boolean;
    memberships: Array<{
        companyId: number;
        teamId: number;
    }>;
}

export interface CreateAdminUserRequest {
    firstName: string;
    lastName: string;
    email: string;
    companyId: number;
    teamIds: number[];
    isActive: boolean;
    inviteUser: boolean;
}

export interface AdminUserCreateResponse {
    auth0UserId: string;
    email: string;
    firstName: string;
    lastName: string;
    companyId: number;
    organizationId: string;
    teamIds: number[];
    isSso: boolean;
    isActive: boolean;
}

export interface AdminUserSyncResponse {
    success: boolean;
    userId: number;
    jobId: string | null;
    subjectId: string | null;
    reason?: string | null;
}

export interface AdminUserSyncSSEEvent {
    type: string;
    result?: AdminUserSyncResponse;
    error?: string;
    [key: string]: unknown;
}

export interface AdminCompanySyncResponse {
    success: boolean;
    companyId: number;
    organizationId: string | null;
    syncUsersRequested?: boolean;
    users?: AdminBulkUserSyncResponse;
}

interface AdminCompanySyncRequest {
    syncUsers?: boolean;
    unsyncedOnly?: boolean;
}

export interface AdminCompanySyncSSEEvent {
    type: string;
    result?: AdminCompanySyncResponse;
    error?: string;
    [key: string]: unknown;
}

export interface AdminBulkUserSyncResponse {
    success: boolean;
    total: number;
    synced: number;
    skipped: number;
    failed: number;
    failures: Array<{
        userId: number;
        email: string;
        error: string;
    }>;
}

export interface AdminAuth0BulkSyncCandidate {
    companyId: number;
    name: string;
    organizationId: string | null;
    activeUserCount: number;
    eligibleUserCount: number;
    unsyncedUserCount: number;
}

interface AdminAuth0BulkSyncCandidatesResponse {
    candidates: AdminAuth0BulkSyncCandidate[];
}

export type AdminAuth0BulkSyncJobStatus =
    | 'running'
    | 'stopping'
    | 'stopped'
    | 'completed'
    | 'failed';

export interface AdminAuth0BulkSyncCompanySummary {
    companyId: number;
    companyName?: string;
    organizationId?: string | null;
    users?: AdminBulkUserSyncResponse;
    error?: string;
}

export interface AdminAuth0BulkSyncJobSnapshot {
    jobId: string;
    requestedBy?: string;
    status: AdminAuth0BulkSyncJobStatus;
    companyIds: number[];
    unsyncedOnly: boolean;
    totalCompanies: number;
    completedCompanies: number;
    failedCompanies: number;
    skippedCompanies: number;
    totalUsers: number;
    syncedUsers: number;
    skippedUsers: number;
    failedUsers: number;
    currentCompanyId?: number;
    currentCompanyIndex?: number;
    companyResults: AdminAuth0BulkSyncCompanySummary[];
    recentEvents: AdminAuth0BulkSyncEvent[];
    stopRequested: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface AdminAuth0BulkSyncStartRequest {
    companyIds: number[];
    unsyncedOnly?: boolean;
}

export interface AdminAuth0BulkSyncEvent {
    type: string;
    jobId: string;
    timestamp?: string;
    error?: string;
    companyId?: number;
    companyIndex?: number;
    totalCompanies?: number;
    completedCompanies?: number;
    failedCompanies?: number;
    remainingCompanies?: number;
    totalUsers?: number;
    syncedUsers?: number;
    skippedUsers?: number;
    failedUsers?: number;
    externalId?: string;
    submittedUsers?: number;
    status?: string;
    summary?: unknown;
    result?: AdminAuth0BulkSyncCompanySummary;
    [key: string]: unknown;
}

interface AdminIdentityAttentionUser {
    id: number;
    publicId: string;
    subjectId: string | null;
    email: string;
    name: string;
    firstName: string;
    lastName: string;
    isActive: boolean;
    isSso: boolean;
    dateCreated: string;
}

interface AdminIdentityAttentionAuth0User {
    subjectId: string;
    email: string;
    name: string;
    createdAt: string | null;
}

export interface AdminIdentityAttentionAuth0EmailConflictIdentity {
    subjectId: string;
    email: string;
    name: string;
    createdAt: string | null;
    connection: string | null;
    provider: string | null;
}

export interface AdminIdentityAttentionAuth0EmailConflictDbUser {
    id: number;
    publicId: string;
    subjectId: string | null;
    email: string;
    name: string;
}

export interface AdminIdentityAttentionAuth0EmailConflict {
    email: string;
    dbUser: AdminIdentityAttentionAuth0EmailConflictDbUser | null;
    identities: AdminIdentityAttentionAuth0EmailConflictIdentity[];
}

interface AdminIdentityAttentionResponse {
    usersWithoutTeams: PaginatedResponse<AdminIdentityAttentionUser>;
    auth0UsersMissingDbRows: PaginatedResponse<AdminIdentityAttentionAuth0User>;
    auth0EmailConflicts: PaginatedResponse<AdminIdentityAttentionAuth0EmailConflict>;
    syncStats: AdminIdentitySyncStats;
}

export interface AdminIdentitySyncStats {
    activeCompaniesTotal: number;
    companiesSynced: number;
    companiesUnsynced: number;
    activeUsersTotal: number;
    usersSynced: number;
    usersUnsynced: number;
    unsyncedActiveCompanyUsers: number;
}

interface AdminAuth0UserDbSyncResponse {
    success: boolean;
    subjectId: string;
    userId: number | null;
}

interface AdminAuth0IdentityLinkApprovalResponse {
    success: boolean;
    primarySubjectId: string | null;
    userId: number | null;
    email: string | null;
    previousSubjectId: string | null;
    linkedSubjectIds: string[];
}

export interface AdminCreditSowRow {
    id: string;
    companyId: number;
    companyName: string;
    name: string;
    state: string;
    unitMode: 'credit' | 'token';
    startDate: string;
    endDate?: string | null;
    pricingConfigId: string;
    granted: number;
    available: number;
    used: number;
    usagePercent: number;
    committedUsagePercent: number;
    pendingUsagePercent: number;
    daysRemaining?: number | null;
    percentCreditsLeft: number;
}

export interface AdminSowUsageSnapshot {
    granted: number;
    used: number;
    usageCommitted: number;
    usagePending: number;
    usagePercent: number;
    committedUsagePercent: number;
    pendingUsagePercent: number;
}

export interface AdminCreditSowRegisterRow extends AdminCreditSowRow {
    proUsage: AdminSowUsageSnapshot | null;
    pulseUsage: AdminSowUsageSnapshot | null;
}

export interface AdminCreditPricingConfigRow extends PricingConfigSummary {
    activeSowCount: number;
    activeSowGroupCount: number;
}

export interface AdminCreditPulseSubscriptionTier {
    id: string;
    name: string;
    type: string;
    selfServiceBilling: boolean;
}

export type AdminPulseSubscriptionScope = 'company' | 'team';

export interface AdminPulseSubscription {
    id: string;
    scope: AdminPulseSubscriptionScope;
    companyId: number | null;
    teamId: number | null;
    teamName: string | null;
    sowId: string | null;
    pulseSubscriptionTierId: string;
    tierName: string | null;
    tierType: string | null;
    startedAt: string | null;
    expiresAt: string | null;
    totalCredits: number | null;
    numSeats: number;
    createdAt: string;
    updatedAt: string;
}

export interface AdminPulseSubscriptionInput {
    scope: AdminPulseSubscriptionScope;
    teamId?: number | null;
    pulseSubscriptionTierId: string;
    startedAt: string;
    expiresAt: string;
    totalCredits: number;
    numSeats: number;
}

/** Active membership in requested teams (`GET /admin/teams/members`). */
interface AdminTeamMemberRow {
    userId: number;
    email: string;
    firstName: string;
    lastName: string;
    teamIds: number[];
}

interface SyncPulseSeatAssignmentsRequest {
    subscriptionId: string;
    userIds: number[];
}

interface SyncPulseSeatAssignmentsResponse {
    success: boolean;
    subscriptionId: string;
    requestedUsers: number;
    assigned: number;
    unassigned: number;
    alreadyAssigned: number;
    message: string;
}

export interface ChangeSowPulseSeatQuantityRequest {
    targetSeatCount: number;
    effectiveAt: string;
    reason: string;
}

interface ChangeSowPulseSeatQuantityResponse {
    success: boolean;
    subscriptionId: string;
    targetSeatCount: number;
}

interface SowAuditEntrySummary {
    id: string;
    sowId: string;
    action: string;
    preValues: Record<string, unknown> | null;
    postValues: Record<string, unknown> | null;
    createdByUserId: number | null;
    createdAt: string;
}

export interface LedgerEntrySummary {
    id: string;
    creditReservationId?: string | null;
    sowId: string;
    poolType: string;
    type?: string;
    entryType: string;
    state: string;
    amount: string;
    description?: string | null;
    createdAt: string;
    /** Present on persisted ledger rows (SOW ledger API returns full `LedgerEntry` fields). */
    sourceSystem?: string;
    eventType?: string | null;
    idempotencyKey?: string;
    linkedArtifactType?: string | null;
    linkedArtifactId?: string | null;
    linkedLedgerEntryId?: string | null;
    auditReason?: string | null;
    position?: number;
    createdByUserId?: number | null;
    createdByUserName?: string | null;
    createdByUserEmail?: string | null;
}

export interface CreditReservationSummary {
    id: string;
    sourceSystem: string;
    idempotencyKey: string;
    status: string;
    eventType: string | null;
    sowId: string;
    poolType: string;
    teamId?: number | null;
    amount: string;
    description?: string | null;
    auditReason?: string | null;
    linkedArtifactType?: string | null;
    linkedArtifactId?: string | null;
    holdLedgerEntryId?: string | null;
    committedLedgerEntryId?: string | null;
    ledgerEntries?: LedgerEntrySummary[];
    createdAt?: string;
    updatedAt?: string;
}

export interface CreateCreditReservationRequest {
    sourceSystem: string;
    idempotencyKey: string;
    eventType?: string | null;
    occurredAt?: string | null;
    sowId: string;
    poolType: string;
    teamId?: number | null;
    amount: string | number;
    description?: string | null;
    auditReason?: string | null;
    linkedArtifactType?: string | null;
    linkedArtifactId?: string | null;
}

export interface CreditReservationFilters {
    status?: string;
    linkedArtifactType?: string;
    linkedArtifactId?: string;
    sowId?: string;
}

export interface CreditReservationMutationRequest {
    idempotencyKey: string;
    auditReason?: string | null;
}

export interface CreditReservationAdjustmentRequest extends CreditReservationMutationRequest {
    amount: string | number;
    sourceSystem?: 'customer-api' | 'admin-portal' | 'pulse' | 'migration' | 'import';
}

export type CreditEventKind = 'grant' | 'usage' | 'adjustment' | 'refund' | 'rollover' | 'reassign';

type CreditEventState = 'pending' | 'committed' | 'cancelled';

interface CreditEventSummary {
    eventId: string;
    reservationId: string | null;
    kind: CreditEventKind;
    state: CreditEventState;
    sourceSystem: string;
    idempotencyKey: string;
    eventType: string | null;
    sowId: string;
    poolType: string;
    teamId: number | null;
    amount: string;
    description: string | null;
    auditReason: string | null;
    linkedLedgerEntryId: string | null;
    linkedArtifactType: string | null;
    linkedArtifactId: string | null;
}

export interface CreateCreditEventRequest {
    kind: CreditEventKind;
    sourceSystem: string;
    idempotencyKey: string;
    eventType?: string | null;
    occurredAt?: string | null;
    sowId?: string;
    destinationSowId?: string;
    poolType?: string;
    teamId?: number | null;
    destinationTeamId?: number | null;
    amount?: string | number;
    description?: string | null;
    auditReason?: string | null;
    linkedArtifactType?: string | null;
    linkedArtifactId?: string | null;
    linkedLedgerEntryId?: string | null;
    basedOnLedgerEntryId?: string | null;
    artifactUrl?: string | null;
    attributedUserId?: number;
}

function deriveLedgerEntryState(entryType: string | undefined): string {
    return entryType === 'usage_hold' ||
        entryType === 'hold_adjustment' ||
        entryType === 'hold_release'
        ? 'pending'
        : 'committed';
}

function normalizeLedgerEntrySummary(entry: LedgerEntrySummary): LedgerEntrySummary {
    const type = entry.type ?? entry.entryType;
    return {
        ...entry,
        type,
        entryType: entry.entryType ?? type,
        state: entry.state ?? deriveLedgerEntryState(type),
        linkedArtifactType: entry.linkedArtifactType ?? null,
        linkedArtifactId: entry.linkedArtifactId ?? null,
        linkedLedgerEntryId: entry.linkedLedgerEntryId ?? null,
    };
}

function normalizeCreditReservationSummary(
    reservation: CreditReservationSummary,
): CreditReservationSummary {
    const { ledgerEntries: directLedgerEntries, ...rest } = reservation;
    const normalizedEntries = (directLedgerEntries ?? []).map(normalizeLedgerEntrySummary);
    const status = rest.status?.trim();
    const entriesWithReservationState =
        status === 'pending' || status === 'committed' || status === 'cancelled'
            ? normalizedEntries.map((entry) => ({ ...entry, state: status }))
            : normalizedEntries;

    return {
        ...rest,
        ledgerEntries: entriesWithReservationState,
    };
}

function isSowUsageCreditImportValidationResponse(
    response: CreditImportValidationResponse,
): response is SowUsageCreditImportValidationResponse {
    return 'summary' in response && 'rows' in response;
}

function isSowUsageCreditImportApplyResponse(
    response: CreditImportApplyResponse,
): response is SowUsageCreditImportApplyResponse {
    return 'batchId' in response && 'sowId' in response && 'appliedRowCount' in response;
}

function normalizeSowSummary(sow: SowSummary): SowSummary {
    return {
        ...sow,
        balanceSummary: sow.balanceSummary ?? null,
    };
}

interface CreditReservationResponse {
    reservation: CreditReservationSummary;
    ledgerEntries: LedgerEntrySummary[];
    idempotent: boolean;
}

interface CreditEventResponse {
    event: CreditEventSummary;
    ledgerEntries: LedgerEntrySummary[];
    idempotent: boolean;
}

export interface HistoricalLedgerImportPreviewRow {
    source: 'pro' | 'pulse';
    sourceRowId: string;
    displayName: string;
    occurredAt: string;
    amount: string;
    teamId: number;
    matchReason: 'source_team_id' | 'current_user_membership' | 'single_team_company_fallback';
    linkedArtifactType: 'pro_project' | 'pulse';
    linkedArtifactId: string;
    eventType: 'pro_study' | 'pulse';
    poolType: 'pro_credit' | 'pulse_credit';
    idempotencyKey: string;
}

export interface HistoricalLedgerImportSkippedRow {
    source: 'pro' | 'pulse';
    sourceRowId: string;
    displayName: string;
    occurredAt: string;
    amount: string;
    sourceTeamId: number | null;
    userId: number | null;
    skipReason:
        | 'missing_team_mapping'
        | 'ambiguous_team_match'
        | 'already_imported'
        | 'outside_inferred_window'
        | 'missing_pulse_subscription'
        | 'missing_legacy_pro_credit';
    candidateTeamIds?: number[];
    idempotencyKey?: string;
}

export interface CompanyHistoricalLedgerImportConflict {
    code:
        | 'existing_active_sow'
        | 'missing_inferred_window'
        | 'no_importable_rows'
        | 'ambiguous_pulse_subscription';
    message: string;
}

export interface CompanyHistoricalLedgerImportWarning {
    code: 'missing_pulse_subscription';
    message: string;
}

export interface CompanyHistoricalLedgerProspectiveSow {
    companyId: number;
    name: string;
    inferredHistoricalStartDate: string;
    inferredHistoricalEndDate: string;
    startDate: string;
    endDate: string | null;
    defaultSelectedSources: Array<'pro' | 'pulse'>;
    sourceOptions: Array<{
        source: 'pro' | 'pulse';
        startDate: string;
        endDate: string | null;
        importableCount: number;
        pulseSubscription: {
            id: string;
            scope: string;
            companyId: number | null;
            teamId: number | null;
            sowId: string | null;
            pulseSubscriptionTierId: string;
            totalCredits: string;
            numSeats: number;
        } | null;
    }>;
    teamIds: number[];
    pools: Array<{
        poolType: SowCreditPoolType;
        initialAmount: string;
    }>;
    pulseSubscription: {
        id: string;
        scope: string;
        companyId: number | null;
        teamId: number | null;
        sowId: string | null;
        pulseSubscriptionTierId: string;
        totalCredits: string;
        numSeats: number;
    } | null;
    metadata: Record<string, unknown>;
}

export interface CompanyHistoricalLedgerImportPreviewResponse {
    prospectiveSow: CompanyHistoricalLedgerProspectiveSow | null;
    summary: {
        totalCandidatesExamined: number;
        importableCount: number;
        skippedCount: number;
        conflictCount: number;
        candidateAmountsByPoolType: Partial<Record<SowCreditPoolType, string>>;
        importableAmountsByPoolType: Partial<Record<SowCreditPoolType, string>>;
        hasPulseSpend: boolean;
        pulseSpendAmount: string;
        legacyProCreditContracts: {
            totalCredits: string;
            contractStartDate: string | null;
            contractEndDate: string | null;
            totalContractCount: number;
            activeContractCount: number;
            hasActiveContract: boolean;
            requiresActiveContractOverride: boolean;
        };
        countsBySource: {
            pro: number;
            pulse: number;
        };
        countsByResolvedTeam: Record<string, number>;
    };
    importableRows: HistoricalLedgerImportPreviewRow[];
    skippedRows: HistoricalLedgerImportSkippedRow[];
    conflicts: CompanyHistoricalLedgerImportConflict[];
    warnings: CompanyHistoricalLedgerImportWarning[];
    previewSignature: string;
    generatedAt: string;
}

export interface ApplyCompanyHistoricalLedgerImportRequest {
    previewSignature: string;
    pricingConfigId: string;
    name?: string;
    selectedSources?: Array<'pro' | 'pulse'>;
    startDate?: string;
    endDate?: string | null;
    allowMissingActiveLegacyContract?: boolean;
    pools?: Array<{
        poolType: SowCreditPoolType;
        initialAmount: string;
    }>;
}

export interface ApplyCompanyHistoricalLedgerImportResponse {
    sowId: string;
    createdCount: number;
    alreadyImportedCount: number;
    skippedCount: number;
}

export interface CreateSowHistoricalImportPreviewRequest {
    companyId: number;
    teamIds: number[];
    startDate: string;
    endDate: string;
    pricingConfigId: string;
}

export interface CreateSowHistoricalImportPreviewResponse {
    summary: {
        proStudyCount: number;
        totalProCredits: string;
        pulseCount: number;
        totalPulseCredits: string;
        hasPulseSpend: boolean;
    };
    importableRows: HistoricalLedgerImportPreviewRow[];
    skippedRows: HistoricalLedgerImportSkippedRow[];
    reusablePulseSubscriptions: Array<{
        id: string;
        scope: string;
        companyId: number | null;
        teamId: number | null;
        sowId: string | null;
        pulseSubscriptionTierId: string;
        totalCredits: string;
        numSeats: number;
        startedAt: string | null;
        expiresAt: string | null;
    }>;
    recommendedPulseSubscriptionId: string | null;
    pulseSubscriptionSelectionRequired: boolean;
    warnings: Array<{
        code: 'no_reusable_pulse_subscription';
        message: string;
    }>;
    previewSignature: string;
    generatedAt: string;
}

export interface CreateSowRequest {
    companyId: number;
    name: string;
    description?: string | null;
    contractReference?: string | null;
    artifactUrl?: string | null;
    unitMode: 'credit' | 'token';
    startDate: string;
    endDate?: string | null;
    pricingConfigId: string;
    teamIds: number[];
    pools: Array<{
        poolType: SowCreditPoolType;
        initialAmount: string;
        resetPolicy?: Record<string, unknown>;
    }>;
    pulseSubscription?: {
        pulseSubscriptionTierId: string;
        numSeats: number;
        activeFromDate?: string | null;
        activeToDate?: string | null;
    } | null;
    metadata?: Record<string, unknown>;
    historicalImport?: {
        previewSignature: string;
        reusablePulseSubscriptionId?: string | null;
    } | null;
}

export interface UpdateSowRequest {
    name?: string;
    description?: string | null;
    contractReference?: string | null;
    artifactUrl?: string | null;
    startDate?: string;
    endDate?: string | null;
    pricingConfigId?: string;
    metadata?: Record<string, unknown>;
    newPools?: Array<{
        poolType: SowCreditPoolType;
        initialAmount: string;
        resetPolicy?: Record<string, unknown>;
    }>;
    teamIds?: number[];
    reason?: string;
}

export interface SowFilters {
    companyId?: number;
    teamId?: number;
    state?: string;
}

export interface CreatePricingConfigRequest {
    name: string;
    description?: string | null;
    config: PricingConfigPayload;
}

export interface UpdatePricingConfigRequest extends Partial<CreatePricingConfigRequest> {
    reason?: string;
}

export interface CreatePricingConfigVersionRequest extends CreatePricingConfigRequest {
    reason?: string;
}

export interface ReassignLedgerEntryRequest {
    destinationSowId: string;
    auditReason: string;
    poolType?: string;
    amount?: string;
    teamId?: number | null;
}

function toQueryString(params: Record<string, string | number | boolean | undefined>): string {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
            searchParams.set(key, String(value));
        }
    });
    const query = searchParams.toString();
    return query ? `?${query}` : '';
}

// New Auth0 User Profile interfaces
interface Auth0UserProfile {
    user_id: string;
    email: string;
    email_verified: boolean;
    name: string;
    given_name: string;
    family_name: string;
    picture?: string;
    user_metadata: Record<string, unknown>;
    created_at: string;
    updated_at: string;
}

export interface ProfileUpdateRequest {
    given_name?: string;
    family_name?: string;
    name?: string;
    email?: string;
    picture?: string;
    user_metadata?: Record<string, unknown>;
}

export interface ProfileUpdateError {
    error: string;
    details?: string;
    restrictedFields?: string[];
}

export interface Organization {
    id: string;
    name: string;
    display_name?: string;
    metadata?: { company_id?: string; [key: string]: unknown };
    groups?: Array<{
        id: string;
        name: string;
        description?: string | null;
        created_at: string;
        updated_at: string;
    }>;
}

export interface SsoConnectionStatus {
    organizationId: string;
    connectionName: string;
    exists: boolean;
    connection?: {
        id: string;
        name: string;
        strategy: string;
    } | null;
}

export interface EnhancedAuth0User {
    user_id: string;
    email: string;
    name: string;
    picture?: string;
    given_name?: string;
    family_name?: string;
    roles: string[];
    // New fields for database users
    public_id?: string;
    database_id?: number;
    subject_id?: string | null;
    has_auth0_profile?: boolean;
    team_memberships?: TeamMembership[];
    is_active?: boolean;
    date_created?: string;
    has_password?: boolean; // Boolean indicating if user has a password (never return password_hash)
    is_sso?: boolean;
}

export interface TeamMembership {
    team_id: number;
    team_name: string;
    team_role_id: number;
    team_role_name: string;
}

interface DatabaseUsersResponse {
    users: EnhancedAuth0User[];
    includeRoles: boolean;
    summary: {
        total: number;
        synced: number;
        unsynced: number;
    };
}

/** Prefer RFC 5987 `filename*` (UTF-8) when the server sends it alongside `filename`. */
function parseContentDispositionFilename(header: string | null): string | null {
    if (!header?.trim()) {
        return null;
    }
    const star = header.match(/filename\*\s*=\s*[^']*''([^;\s]+)\s*(?:;|$)/i);
    if (star?.[1]) {
        try {
            return decodeURIComponent(star[1]);
        } catch {
            /* use plain filename below */
        }
    }
    const quoted = header.match(/filename\s*=\s*"([^"]+)"/i);
    if (quoted?.[1]) {
        return quoted[1];
    }
    const plain = header.match(/filename\s*=\s*([^;\s]+)\s*(?:;|$)/i);
    if (plain?.[1]) {
        return plain[1].replace(/^"(.*)"$/, '$1');
    }
    return null;
}

/** Mirrors backend `LedgerService.buildLedgerExportFilename` for download naming when `Content-Disposition` is not visible to fetch (CORS). */
function buildLedgerExportFilename(sowName: string, sowId: string): string {
    const noControls = [...sowName.trim()].filter((ch) => (ch.codePointAt(0) ?? 0) > 0x1f).join('');
    const cleaned = noControls
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .toLowerCase()
        .slice(0, 120);
    const base = cleaned.length > 0 ? cleaned : `agreement-${sowId.replace(/-/g, '').slice(0, 8)}`;
    return `${base}_ledger.csv`;
}

class ApiClient {
    private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
        const response = await fetch(`${API_BASE_URL}${path}`, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(await readApiErrorFromResponse(response));
        }

        return response.json();
    }

    async publicSignup(request: PublicSignupRequest): Promise<OnboardingResponse> {
        return this.request<OnboardingResponse>('/signup', {
            method: 'POST',
            body: JSON.stringify(request),
        });
    }

    async inviteCompanyUser(
        accessToken: string,
        companyId: number,
        request: CompanyInviteRequest,
    ): Promise<OnboardingResponse> {
        return this.request<OnboardingResponse>(`/companies/${companyId}/invites`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(request),
        });
    }

    // New Auth0 Profile Management Methods
    async getAuth0Profile(accessToken: string): Promise<{ profile: Auth0UserProfile }> {
        return this.request<{ profile: Auth0UserProfile }>('/profile', {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async updateAuth0Profile(
        accessToken: string,
        updates: ProfileUpdateRequest,
    ): Promise<{ message: string; profile: Auth0UserProfile }> {
        return this.request<{ message: string; profile: Auth0UserProfile }>('/profile', {
            method: 'PATCH',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(updates),
        });
    }

    // Removed unsupported endpoint getLegacyUser

    // Removed unsupported endpoint getLegacyUser

    async syncUserData(
        accessToken: string,
        userId: string,
    ): Promise<{
        user: {
            id: number;
            publicId: string;
            first_name: string;
            last_name: string;
            email_address: string;
            is_active: boolean;
            is_admin: boolean;
            isEmailVerified: boolean;
            date_created: string;
            title?: string;
            is_sso: boolean;
            sso_type?: string;
            terms_of_service_accepted: boolean;
            subject_id?: string;
            team_assignment: {
                teams: Array<{
                    id: number;
                    name: string;
                    is_active: boolean;
                    company: {
                        id: number;
                        name: string;
                        industry_id: number;
                        is_active: boolean;
                        url?: string;
                        org_id?: string;
                    };
                }>;
                role: {
                    id: number;
                    name: string;
                };
            } | null;
        };
        sync_result: {
            job_id: string;
            company_was_synced: boolean;
        };
        synced_at: string;
    }> {
        return this.request(`/sync/users/${userId}`, {
            method: 'GET',
            headers: { Authorization: `Bearer ${accessToken}` },
        });
    }

    // Organization Admin endpoints (for cross-org access)
    async getOrganizationUsers(
        accessToken: string,
        orgId: string,
    ): Promise<{ users: EnhancedAuth0User[] }> {
        return this.request<{ users: EnhancedAuth0User[] }>(`/users/org/${orgId}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
    }

    async getOrganizationDatabaseUsers(
        accessToken: string,
        orgId: string,
        includeRoles = true,
    ): Promise<DatabaseUsersResponse> {
        return this.request<DatabaseUsersResponse>(
            `/users/org/${orgId}/database-users?includeRoles=${includeRoles}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async deactivateUser(
        accessToken: string,
        orgId: string,
        userId: string,
    ): Promise<{
        success: boolean;
        message: string;
        user: {
            id: number;
            publicId: string;
            is_active: boolean;
        };
    }> {
        return this.request(`/users/org/${orgId}/users/${userId}/deactivate`, {
            method: 'PATCH',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async getOrganizationUserById(
        accessToken: string,
        orgId: string,
        userId: string,
    ): Promise<{
        user: {
            user_id: string;
            email: string;
            name: string;
            given_name?: string;
            family_name?: string;
            picture?: string;
            nickname?: string;
            created_at: string;
            updated_at: string;
            login_count: number;
            email_verified: boolean;
            user_metadata: Record<string, unknown>;
            app_metadata: Record<string, unknown>;
            identities: Record<string, unknown>[];
            roles: string[];
            organization_id: string;
        };
    }> {
        return this.request<{
            user: {
                user_id: string;
                email: string;
                name: string;
                given_name?: string;
                family_name?: string;
                picture?: string;
                nickname?: string;
                created_at: string;
                updated_at: string;
                login_count: number;
                email_verified: boolean;
                user_metadata: Record<string, unknown>;
                app_metadata: Record<string, unknown>;
                identities: Record<string, unknown>[];
                roles: string[];
                organization_id: string;
            };
        }>(`/users/org/${orgId}/${encodeURIComponent(userId)}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
    }

    /** Assign or remove one Manage-assignable Auth0 role; returns the member's roles afterwards. */
    async setOrganizationUserRole(
        accessToken: string,
        orgId: string,
        userId: string,
        roleName: string,
        assigned: boolean,
    ): Promise<{ success: boolean; roles: string[] }> {
        return this.request<{ success: boolean; roles: string[] }>(
            `/organizations/${encodeURIComponent(orgId)}/users/${encodeURIComponent(userId)}/roles/${encodeURIComponent(roleName)}`,
            {
                method: assigned ? 'POST' : 'DELETE',
                headers: { Authorization: `Bearer ${accessToken}` },
            },
        );
    }

    async getOrganizations(accessToken: string): Promise<Organization[]> {
        return this.request<Organization[]>(`/organizations`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
    }

    async getOrganizationById(accessToken: string, orgId: string): Promise<Organization> {
        return this.request<Organization>(`/organizations/${encodeURIComponent(orgId)}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
    }

    // SSE endpoint for syncing all unsynced users in an organization
    async syncAllUnsyncedUsersSSE(
        accessToken: string,
        orgId: string,
        onMessage: (data: unknown) => void,
        onError?: (error: Error) => void,
        onComplete?: () => void,
    ): Promise<() => void> {
        let aborted = false;

        const runSync = async () => {
            try {
                onMessage({ type: 'start', totalUsers: 0, message: 'Starting user sync' });
                const result = await this.syncAdminUsersToAuth0(accessToken, {
                    organizationId: orgId,
                    unsyncedOnly: true,
                });
                if (aborted) {
                    return;
                }
                onMessage({
                    type: result.success ? 'complete' : 'error',
                    totalUsers: result.total,
                    syncedCount: result.synced,
                    errors: result.failures,
                    message: `Synced ${result.synced} of ${result.total} users`,
                });
                onComplete?.();
            } catch (error) {
                if (aborted) {
                    return;
                }
                onError?.(error as Error);
                onComplete?.();
            }
        };

        runSync();

        return () => {
            aborted = true;
        };
    }

    async getSelfServiceProfiles(accessToken: string): Promise<{
        success: boolean;
        profiles: Array<{
            id: string;
            name: string;
            description?: string;
            branding?: unknown;
            user_attributes?: unknown;
            allowed_strategies?: string[];
        }>;
        message: string;
    }> {
        return this.request('/sso/profiles', {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async getSSOTicket(
        accessToken: string,
        orgId: string,
    ): Promise<{
        success: boolean;
        hasTicket: boolean;
        ticket?: {
            ticketUrl: string;
            ticketId: string;
            issuedAt: string;
            expiresAt: string;
            organizationDisplayName: string;
        };
        message: string;
    }> {
        return this.request(`/sso/${orgId}/ticket`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async revokeSSOTicket(
        accessToken: string,
        orgId: string,
    ): Promise<{
        success: boolean;
        message: string;
        revokedTicketId?: string;
    }> {
        return this.request(`/sso/${orgId}/ticket`, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async generateSSOTicket(
        accessToken: string,
        orgId: string,
        profileId: string,
        domainAliases?: string[],
    ): Promise<{
        success: boolean;
        ticketUrl: string;
        orgId: string;
        organizationDisplayName: string;
        connectionName: string;
        expiresIn: string;
        ticketExpiresAt?: string;
        message: string;
        ticket?: string;
    }> {
        return this.request(`/sso/${orgId}/generate-ticket`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                profileId,
                domainAliases,
            }),
        });
    }

    async getSSOConnectionStatus(
        accessToken: string,
        orgId: string,
    ): Promise<{
        success: boolean;
        data: SsoConnectionStatus;
    }> {
        return this.request(`/sso/${orgId}/connection/status`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async createStripeCheckout(
        accessToken: string,
        priceLookupKey: string,
        quantity: number = 1,
    ): Promise<{ sessionId: string; url: string }> {
        return this.request<{ sessionId: string; url: string }>('/subscriptions/checkout', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ priceLookupKey, quantity }),
        });
    }

    async createCustomerPortalSession(accessToken: string): Promise<{ url: string }> {
        return this.request<{ url: string }>('/subscriptions/portal', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async syncAfterStripeSuccess(
        accessToken: string,
    ): Promise<{ success: boolean; status?: string; subscriptionId?: string; synced?: boolean }> {
        return this.request<{
            success: boolean;
            status?: string;
            subscriptionId?: string;
            synced?: boolean;
        }>(`/subscriptions/sync-after-success`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async createFreeTrial(accessToken: string): Promise<{
        success: boolean;
        subscriptionId: string;
        status: string;
        trialEnd: number;
    }> {
        return this.request<{
            success: boolean;
            subscriptionId: string;
            status: string;
            trialEnd: number;
        }>('/subscriptions/free-trial', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async getAdminCompanies(
        accessToken: string,
        params: AdminListParams = {},
    ): Promise<PaginatedResponse<AdminCompanyRow>> {
        return this.request<PaginatedResponse<AdminCompanyRow>>(
            `/admin/companies${toQueryString({
                query: params.query,
                page: params.page,
                pageSize: params.pageSize,
                ...(params.activeCompaniesOnly ? { activeCompaniesOnly: true } : {}),
            })}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async getAdminCompany(accessToken: string, companyId: number): Promise<AdminCompanyDetail> {
        return this.request<AdminCompanyDetail>(`/admin/companies/${companyId}`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async getAdminCompanyAdmins(
        accessToken: string,
        companyId: number,
    ): Promise<AdminCompanyAdminsResponse> {
        return this.request<AdminCompanyAdminsResponse>(
            `/admin/companies/${companyId}/company-admins`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async updateAdminCompanyUserAdmin(
        accessToken: string,
        companyId: number,
        userId: number,
        request: UpdateAdminCompanyUserAdminRequest,
    ): Promise<AdminCompanyUserAdminUpdateResponse> {
        return this.request<AdminCompanyUserAdminUpdateResponse>(
            `/admin/companies/${companyId}/users/${userId}/company-admin`,
            {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(request),
            },
        );
    }

    async getAdminUsers(
        accessToken: string,
        params: AdminListParams = {},
    ): Promise<PaginatedResponse<AdminUserRow>> {
        return this.request<PaginatedResponse<AdminUserRow>>(
            `/admin/users${toQueryString({
                query: params.query,
                page: params.page,
                pageSize: params.pageSize,
                companyId: params.companyId,
                ...(params.activeUsersOnly ? { activeUsersOnly: true } : {}),
            })}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async getAdminUserEditOptions(
        accessToken: string,
        params: AdminListParams = {},
    ): Promise<PaginatedResponse<AdminUserEditCompanyOption>> {
        return this.request<PaginatedResponse<AdminUserEditCompanyOption>>(
            `/admin/user-edit-options${toQueryString({
                query: params.query,
                page: params.page,
                pageSize: params.pageSize,
            })}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async getAdminIdentityAttention(
        accessToken: string,
        params: AdminListParams = {},
    ): Promise<AdminIdentityAttentionResponse> {
        return this.request<AdminIdentityAttentionResponse>(
            `/admin/identity-attention${toQueryString({
                query: params.query,
                page: params.page,
                pageSize: params.pageSize,
            })}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async getAdminAuth0BulkSyncCandidates(
        accessToken: string,
        unsyncedOnly = true,
    ): Promise<AdminAuth0BulkSyncCandidatesResponse> {
        return this.request<AdminAuth0BulkSyncCandidatesResponse>(
            `/admin/identity-attention/auth0-sync-candidates${toQueryString({ unsyncedOnly })}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async startAdminAuth0BulkSyncJob(
        accessToken: string,
        request: AdminAuth0BulkSyncStartRequest,
    ): Promise<AdminAuth0BulkSyncJobSnapshot> {
        return this.request<AdminAuth0BulkSyncJobSnapshot>(
            '/admin/identity-attention/auth0-sync-jobs',
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(request),
            },
        );
    }

    async getAdminAuth0BulkSyncJob(
        accessToken: string,
        jobId: string,
    ): Promise<AdminAuth0BulkSyncJobSnapshot> {
        return this.request<AdminAuth0BulkSyncJobSnapshot>(
            `/admin/identity-attention/auth0-sync-jobs/${encodeURIComponent(jobId)}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async stopAdminAuth0BulkSyncJob(
        accessToken: string,
        jobId: string,
    ): Promise<AdminAuth0BulkSyncJobSnapshot> {
        return this.request<AdminAuth0BulkSyncJobSnapshot>(
            `/admin/identity-attention/auth0-sync-jobs/${encodeURIComponent(jobId)}/stop`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async streamAdminAuth0BulkSyncJob(
        accessToken: string,
        jobId: string,
        onMessage: (data: AdminAuth0BulkSyncEvent) => void,
        onError?: (error: Error) => void,
        onComplete?: () => void,
    ): Promise<() => void> {
        return streamJsonSse<AdminAuth0BulkSyncEvent>({
            url: `${API_BASE_URL}/admin/identity-attention/auth0-sync-jobs/${encodeURIComponent(jobId)}/events`,
            requestInit: {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    Accept: 'text/event-stream',
                    'Cache-Control': 'no-cache',
                },
                credentials: 'include',
            },
            onMessage,
            onError,
            onComplete: () => onComplete?.(),
            isTerminalEvent: (data) =>
                data.type === 'job_complete' ||
                data.type === 'job_stopped' ||
                data.type === 'job_error',
            getErrorMessage: (data) =>
                data.type === 'job_error' ? data.error || 'Bulk Auth0 sync failed' : undefined,
        });
    }

    async startAdminAuth0BulkSyncJobSSE(
        accessToken: string,
        request: AdminAuth0BulkSyncStartRequest,
        onMessage: (data: AdminAuth0BulkSyncEvent) => void,
        onError?: (error: Error) => void,
        onComplete?: () => void,
    ): Promise<() => void> {
        return streamJsonSse<AdminAuth0BulkSyncEvent>({
            url: `${API_BASE_URL}/admin/identity-attention/auth0-sync-jobs`,
            requestInit: {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    Accept: 'text/event-stream',
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache',
                },
                credentials: 'include',
                body: JSON.stringify(request),
            },
            onMessage,
            onError,
            onComplete: () => onComplete?.(),
            isTerminalEvent: (data) =>
                data.type === 'job_complete' ||
                data.type === 'job_stopped' ||
                data.type === 'job_error',
            getErrorMessage: (data) =>
                data.type === 'job_error' ? data.error || 'Bulk Auth0 sync failed' : undefined,
        });
    }

    async syncAdminAuth0UserToDb(
        accessToken: string,
        subjectId: string,
    ): Promise<AdminAuth0UserDbSyncResponse> {
        return this.request<AdminAuth0UserDbSyncResponse>(
            `/admin/identity-attention/auth0-users/${encodeURIComponent(subjectId)}/sync-db`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async approveAdminAuth0IdentityGroupLink(
        accessToken: string,
        email: string,
    ): Promise<AdminAuth0IdentityLinkApprovalResponse> {
        return this.request<AdminAuth0IdentityLinkApprovalResponse>(
            `/admin/identity-attention/auth0-email-conflicts/${encodeURIComponent(email)}/approve-link`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async updateAdminUser(
        accessToken: string,
        userId: number,
        request: UpdateAdminUserRequest,
    ): Promise<AdminUserUpdateResult> {
        return this.request<AdminUserUpdateResult>(`/admin/users/${userId}`, {
            method: 'PATCH',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(request),
        });
    }

    async createAdminUser(
        accessToken: string,
        request: CreateAdminUserRequest,
    ): Promise<AdminUserCreateResponse> {
        return this.request<AdminUserCreateResponse>('/admin/users', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(request),
        });
    }

    async createAdminCompany(
        accessToken: string,
        request: CreateAdminCompanyRequest,
    ): Promise<AdminCompanyCreateResponse> {
        return this.request<AdminCompanyCreateResponse>('/admin/companies', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(request),
        });
    }

    async updateAdminCompany(
        accessToken: string,
        companyId: number,
        request: UpdateAdminCompanyRequest,
    ): Promise<AdminCompanyCreateResponse> {
        return this.request<AdminCompanyCreateResponse>(`/admin/companies/${companyId}`, {
            method: 'PATCH',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(request),
        });
    }

    async syncAdminUserToAuth0(
        accessToken: string,
        userId: number,
    ): Promise<AdminUserSyncResponse> {
        return this.request<AdminUserSyncResponse>(`/admin/users/${userId}/sync-auth0`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async syncAdminUserToAuth0SSE(
        accessToken: string,
        userId: number,
        onMessage: (data: AdminUserSyncSSEEvent) => void,
        onError?: (error: Error) => void,
        onComplete?: (result?: AdminUserSyncResponse) => void,
    ): Promise<() => void> {
        return streamJsonSse<AdminUserSyncSSEEvent, AdminUserSyncResponse>({
            url: `${API_BASE_URL}/admin/users/${userId}/sync-auth0`,
            requestInit: {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    Accept: 'text/event-stream',
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache',
                },
                credentials: 'include',
            },
            onMessage,
            onError,
            onComplete,
            isTerminalEvent: (data) => data.type === 'complete' || data.type === 'error',
            getErrorMessage: (data) =>
                data.type === 'error' ? data.error || 'User Auth0 sync failed' : undefined,
            getCompletionResult: (data) => data.result,
        });
    }

    async syncAdminCompanyToAuth0(
        accessToken: string,
        companyId: number,
        request: AdminCompanySyncRequest = {},
    ): Promise<AdminCompanySyncResponse> {
        return this.request<AdminCompanySyncResponse>(`/admin/companies/${companyId}/sync-auth0`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(request),
        });
    }

    async syncAdminCompanyToAuth0SSE(
        accessToken: string,
        companyId: number,
        request: AdminCompanySyncRequest = {},
        onMessage: (data: AdminCompanySyncSSEEvent) => void,
        onError?: (error: Error) => void,
        onComplete?: (result?: AdminCompanySyncResponse) => void,
    ): Promise<() => void> {
        return streamJsonSse<AdminCompanySyncSSEEvent, AdminCompanySyncResponse>({
            url: `${API_BASE_URL}/admin/companies/${companyId}/sync-auth0`,
            requestInit: {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    Accept: 'text/event-stream',
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache',
                },
                credentials: 'include',
                body: JSON.stringify(request),
            },
            onMessage,
            onError,
            onComplete,
            isTerminalEvent: (data) => data.type === 'complete' || data.type === 'error',
            getErrorMessage: (data) =>
                data.type === 'error' ? data.error || 'Company Auth0 sync failed' : undefined,
            getCompletionResult: (data) => data.result,
        });
    }

    async syncAdminUsersToAuth0(
        accessToken: string,
        request: { companyId?: number; organizationId?: string; unsyncedOnly?: boolean },
    ): Promise<AdminBulkUserSyncResponse> {
        return this.request<AdminBulkUserSyncResponse>('/admin/users/sync-auth0', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(request),
        });
    }

    async getAdminCreditSows(
        accessToken: string,
        params: AdminCreditSowParams = {},
    ): Promise<PaginatedResponse<AdminCreditSowRegisterRow>> {
        return this.request<PaginatedResponse<AdminCreditSowRegisterRow>>(
            `/admin/credit/sows${toQueryString({
                query: params.query,
                page: params.page,
                pageSize: params.pageSize,
                state: params.state,
                ...(params.activeSowsOnly ? { activeSowsOnly: true } : {}),
                sortBy: params.sortBy,
            })}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async getAdminCreditPricingConfigs(
        accessToken: string,
        params: AdminCreditPricingConfigParams = {},
    ): Promise<PaginatedResponse<AdminCreditPricingConfigRow>> {
        return this.request<PaginatedResponse<AdminCreditPricingConfigRow>>(
            `/admin/credit/pricing-configs${toQueryString({
                query: params.query,
                page: params.page,
                pageSize: params.pageSize,
                includeInactive: params.includeInactive || undefined,
            })}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async getAdminProfessionalServices(
        accessToken: string,
        params: AdminListParams = {},
    ): Promise<PaginatedResponse<AdminProfessionalServiceRow>> {
        return this.request<PaginatedResponse<AdminProfessionalServiceRow>>(
            `/admin/professional-services${toQueryString({
                query: params.query,
                page: params.page,
                pageSize: params.pageSize,
            })}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async createAdminProfessionalService(
        accessToken: string,
        request: AdminProfessionalServiceMutationRequest,
    ): Promise<{ professionalService: AdminProfessionalServiceRow }> {
        return this.request<{ professionalService: AdminProfessionalServiceRow }>(
            '/admin/professional-services',
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(request),
            },
        );
    }

    async updateAdminProfessionalService(
        accessToken: string,
        professionalServiceId: string,
        request: AdminProfessionalServiceMutationRequest,
    ): Promise<{ professionalService: AdminProfessionalServiceRow }> {
        return this.request<{ professionalService: AdminProfessionalServiceRow }>(
            `/admin/professional-services/${encodeURIComponent(professionalServiceId)}`,
            {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(request),
            },
        );
    }

    async deactivateAdminProfessionalService(
        accessToken: string,
        professionalServiceId: string,
    ): Promise<{ professionalService: AdminProfessionalServiceRow }> {
        return this.request<{ professionalService: AdminProfessionalServiceRow }>(
            `/admin/professional-services/${encodeURIComponent(professionalServiceId)}/deactivate`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async getAdminPricingConfigActiveSows(
        accessToken: string,
        pricingConfigId: string,
    ): Promise<{ sows: AdminCreditSowRow[] }> {
        return this.request<{ sows: AdminCreditSowRow[] }>(
            `/admin/credit/pricing-configs/${encodeURIComponent(pricingConfigId)}/active-sows`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async getAdminPricingConfigGroupActiveSows(
        accessToken: string,
        pricingConfigGroupId: string,
    ): Promise<{ sows: AdminCreditSowRow[] }> {
        return this.request<{ sows: AdminCreditSowRow[] }>(
            `/admin/credit/pricing-config-groups/${encodeURIComponent(pricingConfigGroupId)}/active-sows`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async getAdminCreditPulseSubscriptionTiers(
        accessToken: string,
        options?: { forSowCreation?: boolean },
    ): Promise<{ tiers: AdminCreditPulseSubscriptionTier[] }> {
        const query = options?.forSowCreation === true ? '?forSowCreation=true' : '';
        return this.request<{ tiers: AdminCreditPulseSubscriptionTier[] }>(
            `/admin/credit/pulse-subscription-tiers${query}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async getAdminCompanyPulseSubscriptions(
        accessToken: string,
        companyId: number,
    ): Promise<{ subscriptions: AdminPulseSubscription[] }> {
        return this.request<{ subscriptions: AdminPulseSubscription[] }>(
            `/admin/credit/companies/${companyId}/pulse-subscriptions`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async createAdminCompanyPulseSubscription(
        accessToken: string,
        companyId: number,
        request: AdminPulseSubscriptionInput,
    ): Promise<{ subscription: AdminPulseSubscription }> {
        return this.request<{ subscription: AdminPulseSubscription }>(
            `/admin/credit/companies/${companyId}/pulse-subscriptions`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(request),
            },
        );
    }

    async updateAdminCompanyPulseSubscription(
        accessToken: string,
        companyId: number,
        subscriptionId: string,
        request: AdminPulseSubscriptionInput,
    ): Promise<{ subscription: AdminPulseSubscription }> {
        return this.request<{ subscription: AdminPulseSubscription }>(
            `/admin/credit/companies/${companyId}/pulse-subscriptions/${encodeURIComponent(
                subscriptionId,
            )}`,
            {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(request),
            },
        );
    }

    async getAdminTeamMembers(
        accessToken: string,
        teamIds: number[],
    ): Promise<{ users: AdminTeamMemberRow[] }> {
        if (teamIds.length === 0) {
            return { users: [] };
        }
        const query = teamIds.map((id) => `teamIds=${encodeURIComponent(String(id))}`).join('&');
        return this.request<{ users: AdminTeamMemberRow[] }>(`/admin/teams/members?${query}`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async getSows(accessToken: string, filters: SowFilters = {}): Promise<{ sows: SowSummary[] }> {
        const response = await this.request<{ sows: SowSummary[] }>(
            `/sows${toQueryString({
                companyId: filters.companyId,
                teamId: filters.teamId,
                state: filters.state,
            })}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
        return {
            sows: (response.sows ?? []).map(normalizeSowSummary),
        };
    }

    async getSow(accessToken: string, sowId: string): Promise<{ sow: SowSummary }> {
        const response = await this.request<{ sow: SowSummary }>(`/sows/${sowId}`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
        return {
            sow: normalizeSowSummary(response.sow),
        };
    }

    async getSowWriteAccess(
        accessToken: string,
        sowId: string,
    ): Promise<{ writeAccess: SowWriteAccessSummary }> {
        return this.request<{ writeAccess: SowWriteAccessSummary }>(`/sows/${sowId}/write-access`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async updateSowWriteAccess(
        accessToken: string,
        sowId: string,
        request: UpdateSowWriteAccessRequest,
    ): Promise<{ writeAccess: SowWriteAccessSummary }> {
        return this.request<{ writeAccess: SowWriteAccessSummary }>(`/sows/${sowId}/write-access`, {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(request),
        });
    }

    async syncPulseSeatAssignments(
        accessToken: string,
        payload: SyncPulseSeatAssignmentsRequest,
    ): Promise<SyncPulseSeatAssignmentsResponse> {
        const userIds = [
            ...new Set(
                payload.userIds.map((id) =>
                    typeof id === 'number' && Number.isFinite(id)
                        ? Math.trunc(id)
                        : parseInt(String(id).trim(), 10),
                ),
            ),
        ].filter((n) => Number.isFinite(n) && n >= 1 && Number.isSafeInteger(n));

        return this.request<SyncPulseSeatAssignmentsResponse>('/subscriptions/seat-assignments', {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                subscriptionId: payload.subscriptionId,
                userIds,
            }),
        });
    }

    async changeSowPulseSeatQuantity(
        accessToken: string,
        sowId: string,
        payload: ChangeSowPulseSeatQuantityRequest,
    ): Promise<ChangeSowPulseSeatQuantityResponse> {
        return this.request<ChangeSowPulseSeatQuantityResponse>(
            `/sows/${sowId}/pulse-seats/change-quantity`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(payload),
            },
        );
    }

    async getSowLedger(
        accessToken: string,
        sowId: string,
    ): Promise<{ ledgerEntries: LedgerEntrySummary[] }> {
        const response = await this.request<{ ledgerEntries: LedgerEntrySummary[] }>(
            `/sows/${sowId}/ledger`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
        return {
            ledgerEntries: response.ledgerEntries.map(normalizeLedgerEntrySummary),
        };
    }

    async getSowAudit(
        accessToken: string,
        sowId: string,
    ): Promise<{ auditEntries: SowAuditEntrySummary[] }> {
        return this.request<{ auditEntries: SowAuditEntrySummary[] }>(`/sows/${sowId}/audit`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async getSowPricingConfig(
        accessToken: string,
        sowId: string,
    ): Promise<{ pricingConfig: PricingConfigSummary }> {
        return this.request<{ pricingConfig: PricingConfigSummary }>(
            `/sows/${encodeURIComponent(sowId)}/pricing-config`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async getPricingConfigs(
        accessToken: string,
        includeInactive = false,
    ): Promise<{ pricingConfigs: PricingConfigSummary[] }> {
        return this.request<{ pricingConfigs: PricingConfigSummary[] }>(
            `/pricing-configs${toQueryString({ includeInactive: includeInactive || undefined })}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async createPricingConfig(
        accessToken: string,
        request: CreatePricingConfigRequest,
    ): Promise<{ pricingConfig: PricingConfigSummary }> {
        return this.request<{ pricingConfig: PricingConfigSummary }>('/pricing-configs', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(request),
        });
    }

    async updatePricingConfig(
        accessToken: string,
        id: string,
        request: UpdatePricingConfigRequest,
    ): Promise<{ pricingConfig: PricingConfigSummary }> {
        return this.request<{ pricingConfig: PricingConfigSummary }>(`/pricing-configs/${id}`, {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(request),
        });
    }

    async createPricingConfigVersion(
        accessToken: string,
        id: string,
        request: CreatePricingConfigVersionRequest,
    ): Promise<{ pricingConfig: PricingConfigSummary }> {
        return this.request<{ pricingConfig: PricingConfigSummary }>(
            `/pricing-configs/${id}/versions`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(request),
            },
        );
    }

    async clonePricingConfig(
        accessToken: string,
        id: string,
    ): Promise<{ pricingConfig: PricingConfigSummary }> {
        return this.request<{ pricingConfig: PricingConfigSummary }>(
            `/pricing-configs/${id}/clone`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async deactivatePricingConfig(
        accessToken: string,
        id: string,
    ): Promise<{ pricingConfig: PricingConfigSummary }> {
        return this.request<{ pricingConfig: PricingConfigSummary }>(
            `/pricing-configs/${id}/deactivate`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async createSow(accessToken: string, request: CreateSowRequest): Promise<{ sow: SowSummary }> {
        return this.request<{ sow: SowSummary }>('/sows', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(request),
        });
    }

    async previewCreateSowHistoricalImport(
        accessToken: string,
        request: CreateSowHistoricalImportPreviewRequest,
    ): Promise<CreateSowHistoricalImportPreviewResponse> {
        return this.request<CreateSowHistoricalImportPreviewResponse>(
            '/sows/historical-import/preview',
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(request),
            },
        );
    }

    async updateSow(
        accessToken: string,
        sowId: string,
        request: UpdateSowRequest,
    ): Promise<{ sow: SowSummary }> {
        return this.request<{ sow: SowSummary }>(`/sows/${sowId}`, {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(request),
        });
    }

    async archiveSow(accessToken: string, sowId: string): Promise<{ sow: SowSummary }> {
        return this.request<{ sow: SowSummary }>(`/sows/${sowId}/archive`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async expireSow(accessToken: string, sowId: string): Promise<{ sow: SowSummary }> {
        return this.request<{ sow: SowSummary }>(`/sows/${sowId}/expire`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async createCreditEvent(
        accessToken: string,
        request: CreateCreditEventRequest,
    ): Promise<CreditEventResponse> {
        return this.request<CreditEventResponse>('/credit-events', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(request),
        });
    }

    async createCreditReservation(
        accessToken: string,
        request: CreateCreditReservationRequest,
    ): Promise<CreditReservationResponse> {
        return this.request<CreditReservationResponse>('/credit-reservations', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(request),
        });
    }

    async listCreditReservations(
        accessToken: string,
        filters: CreditReservationFilters = {},
    ): Promise<{ reservations: CreditReservationSummary[] }> {
        const response = await this.request<{ reservations: CreditReservationSummary[] }>(
            `/credit-reservations${toQueryString({
                status: filters.status,
                linkedArtifactType: filters.linkedArtifactType,
                linkedArtifactId: filters.linkedArtifactId,
                sowId: filters.sowId,
            })}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
        return {
            reservations: (response.reservations ?? []).map(normalizeCreditReservationSummary),
        };
    }

    async getCreditReservation(
        accessToken: string,
        reservationId: string,
    ): Promise<{ reservation: CreditReservationSummary }> {
        const response = await this.request<{ reservation: CreditReservationSummary }>(
            `/credit-reservations/${reservationId}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
        return {
            reservation: normalizeCreditReservationSummary(response.reservation),
        };
    }

    async commitCreditReservation(
        accessToken: string,
        reservationId: string,
        request: CreditReservationMutationRequest,
    ): Promise<CreditReservationResponse> {
        return this.request<CreditReservationResponse>(
            `/credit-reservations/${reservationId}/commit`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(request),
            },
        );
    }

    async adjustCreditReservation(
        accessToken: string,
        reservationId: string,
        request: CreditReservationAdjustmentRequest,
    ): Promise<CreditReservationResponse> {
        return this.request<CreditReservationResponse>(
            `/credit-reservations/${reservationId}/adjust`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(request),
            },
        );
    }

    async cancelCreditReservation(
        accessToken: string,
        reservationId: string,
        request: CreditReservationMutationRequest,
    ): Promise<CreditReservationResponse> {
        return this.request<CreditReservationResponse>(
            `/credit-reservations/${reservationId}/cancel`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(request),
            },
        );
    }

    async getLedgerEntryHistory(
        accessToken: string,
        entryId: string,
    ): Promise<{ history: LedgerEntrySummary[] }> {
        return this.request<{ history: LedgerEntrySummary[] }>(`/ledger/${entryId}/history`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }

    async reassignLedgerEntry(
        accessToken: string,
        entryId: string,
        request: ReassignLedgerEntryRequest,
    ): Promise<unknown> {
        return this.request(`/ledger/${entryId}/reassign`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(request),
        });
    }

    async previewCompanyHistoricalSowImport(
        accessToken: string,
        companyId: number,
    ): Promise<CompanyHistoricalLedgerImportPreviewResponse> {
        return this.request<CompanyHistoricalLedgerImportPreviewResponse>(
            `/admin/credit/companies/${companyId}/historical-sow-import/preview`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
    }

    async applyCompanyHistoricalSowImport(
        accessToken: string,
        companyId: number,
        request: ApplyCompanyHistoricalLedgerImportRequest,
    ): Promise<ApplyCompanyHistoricalLedgerImportResponse> {
        return this.request<ApplyCompanyHistoricalLedgerImportResponse>(
            `/admin/credit/companies/${companyId}/historical-sow-import/apply`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(request),
            },
        );
    }

    async createCreditImport(
        accessToken: string,
        request: CreateCreditImportRequest,
    ): Promise<CreateCreditImportResponse> {
        return this.request<CreateCreditImportResponse>('/credit-imports', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(request),
        });
    }

    async validateCreditImport(
        accessToken: string,
        batchId: string,
    ): Promise<SowUsageCreditImportValidationResponse> {
        const response = await this.request<CreditImportValidationResponse>(
            `/credit-imports/${batchId}/validate`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
        if (!isSowUsageCreditImportValidationResponse(response)) {
            throw new Error('Expected SOW usage credit import validation response.');
        }
        return response;
    }

    async applyCreditImport(
        accessToken: string,
        batchId: string,
    ): Promise<SowUsageCreditImportApplyResponse> {
        const response = await this.request<CreditImportApplyResponse>(
            `/credit-imports/${batchId}/apply`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
        if (!isSowUsageCreditImportApplyResponse(response)) {
            throw new Error('Expected SOW usage credit import apply response.');
        }
        return response;
    }

    /**
     * Full raw ledger rows as CSV (same data as the "Raw" tab). Uses authenticated fetch
     * so the file can be saved from the SPA; plain navigation cannot send Bearer tokens.
     */
    async downloadSowLedgerExportCsv(
        accessToken: string,
        sowId: string,
        /** Used when the browser cannot read `Content-Disposition` (must match server naming). */
        agreementNameForFilename: string,
    ): Promise<{ blob: Blob; filename: string }> {
        const response = await fetch(`${API_BASE_URL}/sows/${sowId}/ledger/export`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'text/csv',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(await readApiErrorFromResponse(response));
        }

        const fromHeader = parseContentDispositionFilename(
            response.headers.get('Content-Disposition'),
        );
        const filename = fromHeader ?? buildLedgerExportFilename(agreementNameForFilename, sowId);
        const blob = await response.blob();
        return { blob, filename };
    }
}

export const apiClient = new ApiClient();

// Custom hook to use API with Auth0 token
export function useAccessApi() {
    const { getAccessTokenSilently } = useAuth0();

    // Organization Admin API methods (for cross-org access)
    const getOrganizationUsers = async (orgId: string) => {
        const token = await getAccessTokenSilently();
        return apiClient.getOrganizationUsers(token, orgId);
    };

    const getOrganizationDatabaseUsers = async (orgId: string, includeRoles = true) => {
        const token = await getAccessTokenSilently();
        return apiClient.getOrganizationDatabaseUsers(token, orgId, includeRoles);
    };

    const deactivateUser = async (orgId: string, userId: string) => {
        const token = await getAccessTokenSilently();
        return apiClient.deactivateUser(token, orgId, userId);
    };

    const getOrganizationUserById = async (orgId: string, userId: string) => {
        const token = await getAccessTokenSilently();
        return apiClient.getOrganizationUserById(token, orgId, userId);
    };

    const setOrganizationUserRole = async (
        orgId: string,
        userId: string,
        roleName: string,
        assigned: boolean,
    ) => {
        const token = await getAccessTokenSilently();
        return apiClient.setOrganizationUserRole(token, orgId, userId, roleName, assigned);
    };

    const getOrganizations = async () => {
        const token = await getAccessTokenSilently();
        return apiClient.getOrganizations(token);
    };

    const getOrganizationById = async (orgId: string) => {
        const token = await getAccessTokenSilently();
        return apiClient.getOrganizationById(token, orgId);
    };

    // Removed unused helper getLegacyUser (unsupported endpoint)

    const syncUserData = async (userId: string) => {
        const token = await getAccessTokenSilently();
        return apiClient.syncUserData(token, userId);
    };

    const syncAllUnsyncedUsersSSE = async (
        orgId: string,
        onMessage: (data: unknown) => void,
        onError?: (error: Error) => void,
        onComplete?: () => void,
    ) => {
        const token = await getAccessTokenSilently();
        return apiClient.syncAllUnsyncedUsersSSE(token, orgId, onMessage, onError, onComplete);
    };

    // New Auth0 Profile methods
    const getAuth0Profile = async () => {
        const token = await getAccessTokenSilently();
        return apiClient.getAuth0Profile(token);
    };

    const updateAuth0Profile = async (updates: ProfileUpdateRequest) => {
        const token = await getAccessTokenSilently();
        return apiClient.updateAuth0Profile(token, updates);
    };

    const getSows = async (filters: SowFilters = {}) => {
        const token = await getAccessTokenSilently();
        return apiClient.getSows(token, filters);
    };

    const getSow = async (sowId: string) => {
        const token = await getAccessTokenSilently();
        return apiClient.getSow(token, sowId);
    };

    const getSowLedger = async (sowId: string) => {
        const token = await getAccessTokenSilently();
        return apiClient.getSowLedger(token, sowId);
    };

    const getSowAudit = async (sowId: string) => {
        const token = await getAccessTokenSilently();
        return apiClient.getSowAudit(token, sowId);
    };

    const getSowPricingConfig = async (sowId: string) => {
        const token = await getAccessTokenSilently();
        return apiClient.getSowPricingConfig(token, sowId);
    };

    const getPricingConfigs = async (includeInactive = false) => {
        const token = await getAccessTokenSilently();
        return apiClient.getPricingConfigs(token, includeInactive);
    };

    const createPricingConfig = async (request: CreatePricingConfigRequest) => {
        const token = await getAccessTokenSilently();
        return apiClient.createPricingConfig(token, request);
    };

    const updatePricingConfig = async (id: string, request: UpdatePricingConfigRequest) => {
        const token = await getAccessTokenSilently();
        return apiClient.updatePricingConfig(token, id, request);
    };

    const createPricingConfigVersion = async (
        id: string,
        request: CreatePricingConfigVersionRequest,
    ) => {
        const token = await getAccessTokenSilently();
        return apiClient.createPricingConfigVersion(token, id, request);
    };

    const clonePricingConfig = async (id: string) => {
        const token = await getAccessTokenSilently();
        return apiClient.clonePricingConfig(token, id);
    };

    const deactivatePricingConfig = async (id: string) => {
        const token = await getAccessTokenSilently();
        return apiClient.deactivatePricingConfig(token, id);
    };

    const createSow = async (request: CreateSowRequest) => {
        const token = await getAccessTokenSilently();
        return apiClient.createSow(token, request);
    };

    const updateSow = async (sowId: string, request: UpdateSowRequest) => {
        const token = await getAccessTokenSilently();
        return apiClient.updateSow(token, sowId, request);
    };

    const archiveSow = async (sowId: string) => {
        const token = await getAccessTokenSilently();
        return apiClient.archiveSow(token, sowId);
    };

    const expireSow = async (sowId: string) => {
        const token = await getAccessTokenSilently();
        return apiClient.expireSow(token, sowId);
    };

    const createCreditEvent = async (request: CreateCreditEventRequest) => {
        const token = await getAccessTokenSilently();
        return apiClient.createCreditEvent(token, request);
    };

    const createCreditReservation = async (request: CreateCreditReservationRequest) => {
        const token = await getAccessTokenSilently();
        return apiClient.createCreditReservation(token, request);
    };

    const getLedgerEntryHistory = async (entryId: string) => {
        const token = await getAccessTokenSilently();
        return apiClient.getLedgerEntryHistory(token, entryId);
    };

    const adjustCreditReservation = async (
        reservationId: string,
        request: CreditReservationAdjustmentRequest,
    ) => {
        const token = await getAccessTokenSilently();
        return apiClient.adjustCreditReservation(token, reservationId, request);
    };

    const reassignLedgerEntry = async (entryId: string, request: ReassignLedgerEntryRequest) => {
        const token = await getAccessTokenSilently();
        return apiClient.reassignLedgerEntry(token, entryId, request);
    };

    const createCreditImport = async (request: CreateCreditImportRequest) => {
        const token = await getAccessTokenSilently();
        return apiClient.createCreditImport(token, request);
    };

    const validateCreditImport = async (batchId: string) => {
        const token = await getAccessTokenSilently();
        return apiClient.validateCreditImport(token, batchId);
    };

    const applyCreditImport = async (batchId: string) => {
        const token = await getAccessTokenSilently();
        return apiClient.applyCreditImport(token, batchId);
    };

    return {
        getOrganizationUsers,
        getOrganizationDatabaseUsers,
        deactivateUser,
        getOrganizationUserById,
        setOrganizationUserRole,
        getOrganizations,
        getOrganizationById,
        syncUserData,
        syncAllUnsyncedUsersSSE,
        getAuth0Profile,
        updateAuth0Profile,
        getSows,
        getSow,
        getSowLedger,
        getSowAudit,
        getSowPricingConfig,
        getPricingConfigs,
        createPricingConfig,
        updatePricingConfig,
        createPricingConfigVersion,
        clonePricingConfig,
        deactivatePricingConfig,
        createSow,
        updateSow,
        archiveSow,
        expireSow,
        createCreditEvent,
        createCreditReservation,
        adjustCreditReservation,
        getLedgerEntryHistory,
        reassignLedgerEntry,
        createCreditImport,
        validateCreditImport,
        applyCreditImport,
        exportSowLedgerCsv: async (sowId: string, agreementName: string) => {
            const token = await getAccessTokenSilently();
            const { blob, filename } = await apiClient.downloadSowLedgerExportCsv(
                token,
                sowId,
                agreementName,
            );
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(url);
        },
        getSelfServiceProfiles: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.getSelfServiceProfiles(token);
        },
        getSSOTicket: async (orgId: string) => {
            const token = await getAccessTokenSilently();
            return apiClient.getSSOTicket(token, orgId);
        },
        revokeSSOTicket: async (orgId: string) => {
            const token = await getAccessTokenSilently();
            return apiClient.revokeSSOTicket(token, orgId);
        },
        generateSSOTicket: async (orgId: string, profileId: string, domainAliases?: string[]) => {
            const token = await getAccessTokenSilently();
            return apiClient.generateSSOTicket(token, orgId, profileId, domainAliases);
        },
        getSSOConnectionStatus: async (orgId: string) => {
            const token = await getAccessTokenSilently();
            return apiClient.getSSOConnectionStatus(token, orgId);
        },
        createStripeCheckout: async (priceLookupKey: string, quantity: number = 1) => {
            const token = await getAccessTokenSilently();
            return apiClient.createStripeCheckout(token, priceLookupKey, quantity);
        },
        createCustomerPortalSession: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.createCustomerPortalSession(token);
        },
        syncAfterStripeSuccess: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.syncAfterStripeSuccess(token);
        },
        createFreeTrial: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.createFreeTrial(token);
        },
    };
}
